import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  Timestamp,
  orderBy,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Reservation, ReservationStatus } from '../types/reservation';
import { fleetService } from './fleetService';
import { v4 as uuidv4 } from 'uuid';

const RESERVATIONS_COLLECTION = 'reservations';

export const reservationService = {
  async createReservation(data: {
    organizationId: string;
    fleetId: string;
    vehicleId: string;
    userEmail: string;
    startDate: Date;
    endDate: Date;
    purpose: string;
    notes?: string;
  }): Promise<string> {
    try {
      // Vérifier si le véhicule est disponible pour cette période
      const isAvailable = await this.checkVehicleAvailability(
        data.vehicleId,
        data.startDate,
        data.endDate
      );

      if (!isAvailable) {
        throw new Error('Vehicle is not available for the selected period');
      }

      // Vérifier si l'email est autorisé pour cette flotte
      const isEmailValid = await this.validateEmailDomain(
        data.userEmail,
        data.fleetId
      );

      if (!isEmailValid) {
        throw new Error('Email domain is not allowed for this fleet');
      }

      // Récupérer les paramètres de la flotte pour savoir si la validation par email est requise
      const fleet = await fleetService.getFleet(data.fleetId);
      const requiresEmailValidation = fleet?.requireEmailValidation || false;

      // Créer la réservation
      const reservationData: Omit<Reservation, 'id'> = {
        organizationId: data.organizationId,
        fleetId: data.fleetId,
        vehicleId: data.vehicleId,
        userEmail: data.userEmail,
        startDate: Timestamp.fromDate(data.startDate),
        endDate: Timestamp.fromDate(data.endDate),
        status: requiresEmailValidation ? 'pending' : 'confirmed',
        purpose: data.purpose,
        requiresEmailValidation,
        validated: !requiresEmailValidation,
        cancellableByUser: true, // Par défaut, l'utilisateur peut annuler
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      if (data.notes) {
        reservationData.notes = data.notes;
      }

      // Si la validation par email est requise, générer un code de validation
      if (requiresEmailValidation) {
        const validationCode = this.generateRandomCode();
        reservationData.validationCode = validationCode;
        // Le code expire dans 24 heures
        reservationData.validationExpiry = Timestamp.fromDate(
          new Date(Date.now() + 24 * 60 * 60 * 1000)
        );
      }

      const docRef = await addDoc(collection(db, RESERVATIONS_COLLECTION), reservationData);
      
      // Notifier les administrateurs de la nouvelle réservation
      try {
        const { notificationService } = await import('./notificationService');
        await notificationService.notifyAdminsAboutNewReservation(
          data.organizationId,
          docRef.id,
          data.vehicleId,
          data.userEmail
        );
      } catch (notificationError) {
        console.warn('Erreur lors de la notification des administrateurs:', notificationError);
        // Ne pas bloquer la création de la réservation en cas d'erreur de notification
      }
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating reservation:', error);
      throw error;
    }
  },

  async getReservations(
    organizationId: string,
    filters?: {
      fleetId?: string;
      vehicleId?: string;
      status?: ReservationStatus;
      startDate?: Date;
      endDate?: Date;
    }
  ): Promise<Reservation[]> {
    try {
      console.log('getReservations called with:', { organizationId, filters });
      
      let q;
      
      // Si organizationId est vide mais vehicleId est fourni
      if (!organizationId && filters?.vehicleId) {
        console.log('Empty organizationId with vehicleId, getting vehicle info first');
        
        // Récupérer d'abord les informations du véhicule pour obtenir le fleetId
        const vehicleRef = doc(db, 'vehicles', filters.vehicleId);
        const vehicleSnap = await getDoc(vehicleRef);
        
        if (!vehicleSnap.exists()) {
          console.error('Vehicle not found:', filters.vehicleId);
          throw new Error('Vehicle not found');
        }
        
        const vehicleData = vehicleSnap.data();
        console.log('Vehicle data:', vehicleData);
        
        // Vérifier si le véhicule appartient à une flotte
        if (vehicleData.fleetId) {
          console.log('Vehicle belongs to fleet:', vehicleData.fleetId);
          
          // Récupérer les informations de la flotte
          const fleetRef = doc(db, 'fleets', vehicleData.fleetId);
          const fleetSnap = await getDoc(fleetRef);
          
          if (fleetSnap.exists()) {
            const fleetData = fleetSnap.data();
            console.log('Fleet data:', fleetData);
            
            // Vérifier si la flotte a publicLinkEnabled=true
            if (fleetData.publicLinkEnabled) {
              console.log('Fleet has publicLinkEnabled=true, using fleetId in query');
              
              // Construire la requête avec fleetId et vehicleId
              q = query(
                collection(db, RESERVATIONS_COLLECTION),
                where('fleetId', '==', vehicleData.fleetId),
                where('vehicleId', '==', filters.vehicleId)
              );
            } else {
              console.log('Fleet does not have publicLinkEnabled=true');
              // Utiliser uniquement vehicleId dans la requête
              q = query(
                collection(db, RESERVATIONS_COLLECTION),
                where('vehicleId', '==', filters.vehicleId)
              );
            }
          } else {
            console.log('Fleet not found, using only vehicleId in query');
            q = query(
              collection(db, RESERVATIONS_COLLECTION),
              where('vehicleId', '==', filters.vehicleId)
            );
          }
        } else {
          console.log('Vehicle does not belong to a fleet, using only vehicleId in query');
          q = query(
            collection(db, RESERVATIONS_COLLECTION),
            where('vehicleId', '==', filters.vehicleId)
          );
        }
      } else if (organizationId) {
        console.log('Using organizationId in query');
        q = query(
          collection(db, RESERVATIONS_COLLECTION),
          where('organizationId', '==', organizationId)
        );
        
        // Ajouter le filtre fleetId si fourni
        if (filters?.fleetId) {
          console.log('Adding fleetId filter:', filters.fleetId);
          q = query(q, where('fleetId', '==', filters.fleetId));
        }
        
        // Ajouter le filtre vehicleId si fourni
        if (filters?.vehicleId) {
          console.log('Adding vehicleId filter:', filters.vehicleId);
          q = query(q, where('vehicleId', '==', filters.vehicleId));
        }
      } else {
        console.log('No organizationId or vehicleId provided, querying all reservations');
        q = query(collection(db, RESERVATIONS_COLLECTION));
      }

      if (filters?.status) {
        q = query(q, where('status', '==', filters.status));
      }

      // Ajouter un tri par date de début
      q = query(q, orderBy('startDate', 'asc'));

      const querySnapshot = await getDocs(q);
      const reservations = querySnapshot.docs.map(
        doc => ({ id: doc.id, ...doc.data() } as Reservation)
      );

      // Filtrer par date si nécessaire (filtrage côté client car Firestore ne permet pas de filtrer sur plusieurs champs)
      if (filters?.startDate || filters?.endDate) {
        return reservations.filter(reservation => {
          const reservationStart = reservation.startDate.toDate();
          const reservationEnd = reservation.endDate.toDate();

          if (filters.startDate && filters.endDate) {
            // Vérifier si la réservation chevauche la période demandée
            return (
              (reservationStart >= filters.startDate && reservationStart <= filters.endDate) ||
              (reservationEnd >= filters.startDate && reservationEnd <= filters.endDate) ||
              (reservationStart <= filters.startDate && reservationEnd >= filters.endDate)
            );
          } else if (filters.startDate) {
            // Vérifier si la réservation commence après la date demandée
            return reservationStart >= filters.startDate;
          } else if (filters.endDate) {
            // Vérifier si la réservation se termine avant la date demandée
            return reservationEnd <= filters.endDate;
          }

          return true;
        });
      }

      return reservations;
    } catch (error) {
      console.error('Error getting reservations:', error);
      throw error;
    }
  },

  async getReservation(id: string): Promise<Reservation | null> {
    try {
      const docRef = doc(db, RESERVATIONS_COLLECTION, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as Reservation;
      }
      return null;
    } catch (error) {
      console.error('Error getting reservation:', error);
      throw error;
    }
  },

  async updateReservationStatus(
    id: string,
    status: ReservationStatus
  ): Promise<void> {
    try {
      const docRef = doc(db, RESERVATIONS_COLLECTION, id);
      await updateDoc(docRef, {
        status,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating reservation status:', error);
      throw error;
    }
  },

  async checkVehicleAvailability(
    vehicleId: string,
    startDate: Date,
    endDate: Date
  ): Promise<boolean> {
    try {
      console.log('Checking vehicle availability for:', { vehicleId, startDate, endDate });
      
      // Récupérer d'abord les informations du véhicule pour obtenir le fleetId
      const vehicleRef = doc(db, 'vehicles', vehicleId);
      const vehicleSnap = await getDoc(vehicleRef);
      
      if (!vehicleSnap.exists()) {
        console.error('Vehicle not found:', vehicleId);
        throw new Error('Vehicle not found');
      }
      
      const vehicleData = vehicleSnap.data();
      console.log('Vehicle data:', vehicleData);
      
      let q;
      
      // Vérifier si le véhicule appartient à une flotte
      if (vehicleData.fleetId) {
        console.log('Vehicle belongs to fleet:', vehicleData.fleetId);
        
        // Récupérer les informations de la flotte
        const fleetRef = doc(db, 'fleets', vehicleData.fleetId);
        const fleetSnap = await getDoc(fleetRef);
        
        if (fleetSnap.exists()) {
          const fleetData = fleetSnap.data();
          console.log('Fleet data:', fleetData);
          
          // Vérifier si la flotte a publicLinkEnabled=true
          if (fleetData.publicLinkEnabled) {
            console.log('Fleet has publicLinkEnabled=true, using fleetId in query');
            
            // Construire la requête avec fleetId et vehicleId
            q = query(
              collection(db, RESERVATIONS_COLLECTION),
              where('fleetId', '==', vehicleData.fleetId),
              where('vehicleId', '==', vehicleId),
              where('status', '==', 'confirmed')
            );
          } else {
            console.log('Fleet does not have publicLinkEnabled=true');
            // Utiliser uniquement vehicleId dans la requête
            q = query(
              collection(db, RESERVATIONS_COLLECTION),
              where('vehicleId', '==', vehicleId),
              where('status', '==', 'confirmed')
            );
          }
        } else {
          console.log('Fleet not found, using only vehicleId in query');
          q = query(
            collection(db, RESERVATIONS_COLLECTION),
            where('vehicleId', '==', vehicleId),
            where('status', '==', 'confirmed')
          );
        }
      } else {
        console.log('Vehicle does not belong to a fleet, using only vehicleId in query');
        q = query(
          collection(db, RESERVATIONS_COLLECTION),
          where('vehicleId', '==', vehicleId),
          where('status', '==', 'confirmed')
        );
      }

      const querySnapshot = await getDocs(q);
      const reservations = querySnapshot.docs.map(
        doc => ({ id: doc.id, ...doc.data() } as Reservation)
      );

      // Vérifier si une des réservations chevauche la période demandée
      const isOverlapping = reservations.some(reservation => {
        const reservationStart = reservation.startDate.toDate();
        const reservationEnd = reservation.endDate.toDate();

        // Vérifier si les périodes se chevauchent
        return (
          (startDate >= reservationStart && startDate < reservationEnd) ||
          (endDate > reservationStart && endDate <= reservationEnd) ||
          (startDate <= reservationStart && endDate >= reservationEnd)
        );
      });

      return !isOverlapping;
    } catch (error) {
      console.error('Error checking vehicle availability:', error);
      throw error;
    }
  },

  async generateValidationCode(
    reservationId: string,
    email: string
  ): Promise<string> {
    try {
      const reservation = await this.getReservation(reservationId);

      if (!reservation) {
        throw new Error('Reservation not found');
      }

      if (reservation.userEmail !== email) {
        throw new Error('Email does not match reservation');
      }

      const validationCode = this.generateRandomCode();
      const validationExpiry = Timestamp.fromDate(
        new Date(Date.now() + 24 * 60 * 60 * 1000)
      );

      const docRef = doc(db, RESERVATIONS_COLLECTION, reservationId);
      await updateDoc(docRef, {
        validationCode,
        validationExpiry,
        updatedAt: Timestamp.now(),
      });

      return validationCode;
    } catch (error) {
      console.error('Error generating validation code:', error);
      throw error;
    }
  },

  async validateReservation(
    reservationId: string,
    code: string
  ): Promise<boolean> {
    try {
      const reservation = await this.getReservation(reservationId);

      if (!reservation) {
        throw new Error('Reservation not found');
      }

      if (!reservation.validationCode || !reservation.validationExpiry) {
        throw new Error('Reservation does not require validation');
      }

      // Vérifier si le code est valide et n'a pas expiré
      if (
        reservation.validationCode !== code ||
        reservation.validationExpiry.toDate() < new Date()
      ) {
        return false;
      }

      // Mettre à jour la réservation
      const docRef = doc(db, RESERVATIONS_COLLECTION, reservationId);
      await updateDoc(docRef, {
        validated: true,
        status: 'confirmed',
        updatedAt: Timestamp.now(),
      });

      return true;
    } catch (error) {
      console.error('Error validating reservation:', error);
      throw error;
    }
  },

  async validateEmailDomain(
    email: string,
    fleetId: string
  ): Promise<boolean> {
    try {
      const fleet = await fleetService.getFleet(fleetId);

      if (!fleet) {
        throw new Error('Fleet not found');
      }

      // Si aucun domaine n'est spécifié, tous les emails sont autorisés
      if (!fleet.allowedEmailDomains || fleet.allowedEmailDomains.length === 0) {
        return true;
      }

      // Extraire le domaine de l'email
      const domain = email.split('@')[1];

      // Vérifier si le domaine est dans la liste des domaines autorisés
      return fleet.allowedEmailDomains.includes(domain);
    } catch (error) {
      console.error('Error validating email domain:', error);
      throw error;
    }
  },

  // Méthode utilitaire pour générer un code aléatoire
  generateRandomCode(): string {
    // Générer un code de 6 chiffres
    return Math.floor(100000 + Math.random() * 900000).toString();
  },

  // Annulation d'une réservation par l'utilisateur public
  async cancelReservationByUser(
    reservationId: string,
    email: string
  ): Promise<void> {
    try {
      const reservation = await this.getReservation(reservationId);

      if (!reservation) {
        throw new Error('Reservation not found');
      }

      if (reservation.userEmail !== email) {
        throw new Error('Email does not match reservation');
      }

      if (!reservation.cancellableByUser) {
        throw new Error('This reservation cannot be cancelled by the user');
      }

      if (reservation.status === 'cancelled') {
        throw new Error('Reservation is already cancelled');
      }

      const docRef = doc(db, RESERVATIONS_COLLECTION, reservationId);
      await updateDoc(docRef, {
        status: 'cancelled',
        cancelledBy: email,
        cancelledAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error cancelling reservation by user:', error);
      throw error;
    }
  },

  // Suggérer le prochain créneau disponible pour un véhicule
  async suggestNextAvailableSlot(
    vehicleId: string,
    desiredStartDate: Date,
    durationInMinutes: number
  ): Promise<{ startDate: Date; endDate: Date } | null> {
    try {
      // Récupérer toutes les réservations confirmées pour ce véhicule à partir de la date souhaitée
      const q = query(
        collection(db, RESERVATIONS_COLLECTION),
        where('vehicleId', '==', vehicleId),
        where('status', '==', 'confirmed'),
        orderBy('startDate', 'asc')
      );

      const querySnapshot = await getDocs(q);
      const reservations = querySnapshot.docs.map(
        doc => ({ id: doc.id, ...doc.data() } as Reservation)
      );

      // Filtrer uniquement les réservations après la date souhaitée
      const futureReservations = reservations.filter(r =>
        r.startDate.toDate() >= desiredStartDate
      );

      // Chercher le premier créneau disponible
      let currentCheckDate = new Date(desiredStartDate);
      const maxChecks = 100; // Limite pour éviter les boucles infinies
      let checks = 0;

      while (checks < maxChecks) {
        const proposedEndDate = new Date(currentCheckDate.getTime() + durationInMinutes * 60000);

        // Vérifier si ce créneau est disponible
        const isAvailable = await this.checkVehicleAvailability(
          vehicleId,
          currentCheckDate,
          proposedEndDate
        );

        if (isAvailable) {
          return {
            startDate: currentCheckDate,
            endDate: proposedEndDate
          };
        }

        // Si pas disponible, trouver la prochaine fin de réservation
        const blockingReservation = futureReservations.find(r => {
          const rStart = r.startDate.toDate();
          const rEnd = r.endDate.toDate();
          return (currentCheckDate >= rStart && currentCheckDate < rEnd) ||
                 (proposedEndDate > rStart && proposedEndDate <= rEnd) ||
                 (currentCheckDate <= rStart && proposedEndDate >= rEnd);
        });

        if (blockingReservation) {
          // Essayer juste après la fin de cette réservation
          currentCheckDate = new Date(blockingReservation.endDate.toDate().getTime() + 1000);
        } else {
          // Avancer d'une heure
          currentCheckDate = new Date(currentCheckDate.getTime() + 3600000);
        }

        checks++;
      }

      return null; // Aucun créneau trouvé
    } catch (error) {
      console.error('Error suggesting next available slot:', error);
      throw error;
    }
  },

  // Récupérer la disponibilité de tous les véhicules d'une flotte pour une période
  async getVehiclesAvailabilityForPeriod(
    fleetId: string,
    startDate: Date,
    endDate: Date
  ): Promise<Array<{
    vehicleId: string;
    reservations: Array<{
      startDate: Date;
      endDate: Date;
      status: ReservationStatus;
    }>;
  }>> {
    try {
      // Récupérer toutes les réservations de la flotte pour la période
      const q = query(
        collection(db, RESERVATIONS_COLLECTION),
        where('fleetId', '==', fleetId),
        where('status', '==', 'confirmed'),
        orderBy('startDate', 'asc')
      );

      const querySnapshot = await getDocs(q);
      const reservations = querySnapshot.docs.map(
        doc => ({ id: doc.id, ...doc.data() } as Reservation)
      );

      // Filtrer les réservations qui chevauchent la période demandée
      const relevantReservations = reservations.filter(reservation => {
        const reservationStart = reservation.startDate.toDate();
        const reservationEnd = reservation.endDate.toDate();

        return (
          (reservationStart >= startDate && reservationStart <= endDate) ||
          (reservationEnd >= startDate && reservationEnd <= endDate) ||
          (reservationStart <= startDate && reservationEnd >= endDate)
        );
      });

      // Grouper par véhicule
      const vehicleMap = new Map<string, Array<{
        startDate: Date;
        endDate: Date;
        status: ReservationStatus;
      }>>();

      relevantReservations.forEach(reservation => {
        if (!vehicleMap.has(reservation.vehicleId)) {
          vehicleMap.set(reservation.vehicleId, []);
        }
        vehicleMap.get(reservation.vehicleId)!.push({
          startDate: reservation.startDate.toDate(),
          endDate: reservation.endDate.toDate(),
          status: reservation.status
        });
      });

      // Convertir en tableau
      const result = Array.from(vehicleMap.entries()).map(([vehicleId, reservations]) => ({
        vehicleId,
        reservations
      }));

      return result;
    } catch (error) {
      console.error('Error getting vehicles availability for period:', error);
      throw error;
    }
  },
};

export default reservationService;
